import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  SETTINGS_ENTITIES,
  SettingsEntityKey,
  SettingsEntityRowMap,
  getEntityConfig,
} from "./helpers/settings-config";
import { AppThunk, RootState } from "@store/index";
import {
  createContactItemApi,
  createItem,
  deleteContactItemApi,
  deleteItem,
  fetchContactItemsApi,
  fetchItem,
  fetchList,
  restoreItem,
  updateContactItemApi,
  updateItem,
} from "./api";
import { getErrorMessageFromResponse } from "@helpers/get-error-message";
import { CONTACT_ENTITIES, ContactEntityKey } from "./helpers/contacts-config";

const SETTINGS_ENTITY_TO_INVALIDATE_CONTACTS: Partial<
  Record<SettingsEntityKey, ContactEntityKey[]>
> = {
  customs_offices: ["guarantees"],
};

export type ListState<T> = {
  items: T[] | null;
  total: number;
  page: number;
  size: number;
  loading: boolean;
  error: string | null;
  success: string | null;
};

type ContactItemsState = {
  [K in ContactEntityKey]: Record<number, any[]>;
};

type ContactLoadingState = {
  [K in ContactEntityKey]: Record<number, boolean>;
};

type ContactLoadedState = {
  [K in ContactEntityKey]: Record<number, boolean>;
};

/** Cache for contact-type entities (emails, addresses, guarantees) keyed by parent id (customer or office) */
export type ContactItemsCacheState = {
  items: ContactItemsState;
  loading: ContactLoadingState;
  loaded: ContactLoadedState;
  error: string | null;
};

type HydrateContactsPayload = {
  type: ContactEntityKey;
  parentId: number;
  items: any[];
};

type SetContactsLoadingPayload = {
  type: ContactEntityKey;
  parentId: number;
  loading: boolean;
};

type UpsertContactPayload = {
  type: ContactEntityKey;
  parentId: number;
  item: any;
};

type RemoveContactPayload = {
  type: ContactEntityKey;
  parentId: number;
  itemId: number;
};

export type SettingsState = {
  [K in SettingsEntityKey]: ListState<SettingsEntityRowMap[K]> & {
    activeEntity: { id: number } | null;
  };
} & {
  contactItems: ContactItemsCacheState;
} & {
  duplicate: {
    item: SettingsEntityRowMap["apl" | "ppl"] | null;
  };
};

export const getInitialListState = <T>(): ListState<T> & {
  activeEntity: T | null;
} => ({
  items: null,
  total: 0,
  page: 1,
  size: 10,
  loading: false,
  error: null,
  success: null,
  activeEntity: null,
});

export const getInitialContactItemsCacheState = (): ContactItemsCacheState => ({
  items: Object.fromEntries(
    Object.keys(CONTACT_ENTITIES).map((key) => [key, {}]),
  ) as ContactItemsState,
  loading: Object.fromEntries(
    Object.keys(CONTACT_ENTITIES).map((key) => [key, {}]),
  ) as ContactLoadingState,
  loaded: Object.fromEntries(
    Object.keys(CONTACT_ENTITIES).map((key) => [key, {}]),
  ) as ContactLoadedState,
  error: null,
});

export const initialState: SettingsState = {
  ...(Object.fromEntries(
    (Object.keys(SETTINGS_ENTITIES) as SettingsEntityKey[]).map((key) => [
      key,
      getInitialListState<SettingsEntityRowMap[typeof key]>(),
    ]),
  ) as Omit<SettingsState, "contactItems" | "duplicate">),
  contactItems: getInitialContactItemsCacheState(),
  duplicate: {
    item: null,
  },
};

const upsertItem = <T extends { id: number }>(items: T[] = [], item: T) => {
  const exists = items.findIndex((entry) => entry.id === item.id);
  if (exists === -1) {
    return [item, ...items];
  }
  const clone = [...items];
  clone[exists] = item;
  return clone;
};

const settingsSlice = createSlice({
  name: "settings",
  initialState,
  reducers: {
    getStart(state: SettingsState, action: PayloadAction<SettingsEntityKey>) {
      const key = action.payload;
      state[key].loading = true;
      state[key].error = null;
      state[key].success = null;
    },
    getSuccess<K extends SettingsEntityKey>(
      state: SettingsState,
      action: PayloadAction<{
        key: K;
        items: SettingsEntityRowMap[K][];
        total: number;
      }>,
    ) {
      const { key, items, total } = action.payload;
      state[key].items = items as any;
      state[key].total = total;
      state[key].loading = false;
    },
    getFailure(
      state: SettingsState,
      action: PayloadAction<{ key: SettingsEntityKey; error: string }>,
    ) {
      const { key, error } = action.payload;
      state[key].loading = false;
      state[key].error = error;
    },
    setSuccess(
      state: SettingsState,
      action: PayloadAction<{ key: SettingsEntityKey; success: string }>,
    ) {
      const { key, success } = action.payload;
      state[key].success = success;
      state[key].loading = false;
    },
    addCreatedItem<K extends SettingsEntityKey>(
      state: SettingsState,
      action: PayloadAction<{ key: K; item: SettingsEntityRowMap[K] }>,
    ) {
      const { key, item } = action.payload;
      state[key].items = [item as any, ...(state[key].items || [])];
    },
    cleanData(state: SettingsState, action: PayloadAction<SettingsEntityKey>) {
      const key = action.payload;
      state[key] = getInitialListState<
        SettingsEntityRowMap[typeof key]
      >() as any;
    },
    setActiveEntity<K extends SettingsEntityKey>(
      state: SettingsState,
      action: PayloadAction<{ key: K; item: SettingsEntityRowMap[K] }>,
    ) {
      const { key, item } = action.payload;
      state[key].activeEntity = item;
      state[key].loading = false;
    },
    hideWarning(
      state: SettingsState,
      action: PayloadAction<SettingsEntityKey>,
    ) {
      const key = action.payload;
      state[key].error = null;
      state[key].success = null;
    },
    cleanActiveEntity(
      state: SettingsState,
      action: PayloadAction<SettingsEntityKey>,
    ) {
      const key = action.payload;
      state[key].activeEntity = null;
    },
    hydrateContacts(state, action: PayloadAction<HydrateContactsPayload>) {
      const { type, parentId, items } = action.payload;
      state.contactItems.items[type][parentId] = items;
      state.contactItems.loaded[type][parentId] = true;
      state.contactItems.loading[type][parentId] = false;
    },

    setContactsLoading(
      state,
      action: PayloadAction<SetContactsLoadingPayload>,
    ) {
      const { type, parentId, loading } = action.payload;
      state.contactItems.loading[type][parentId] = loading;
    },

    upsertContact(state, action: PayloadAction<UpsertContactPayload>) {
      const { type, parentId, item } = action.payload;
      const current = state.contactItems.items[type][parentId] || [];
      state.contactItems.items[type][parentId] = upsertItem(current, item);
    },

    removeContact(state, action: PayloadAction<RemoveContactPayload>) {
      const { type, parentId, itemId } = action.payload;
      const current = state.contactItems.items[type][parentId] || [];
      state.contactItems.items[type][parentId] = current.filter(
        (i: any) => i.id !== itemId,
      );
    },

    setContactsError(state, action) {
      state.contactItems.error = action.payload;
    },

    invalidateContactItems(state, action: PayloadAction<ContactEntityKey>) {
      const type = action.payload;
      const itemIds = Object.keys(
        state.contactItems.items[type] || {},
      ) as unknown as number[];
      itemIds.forEach((id) => {
        state.contactItems.loaded[type][id] = false;
      });
    },

    setDuplicateServiceItem(
      state,
      action: PayloadAction<{
        key: "apl" | "ppl";
        item: SettingsEntityRowMap["apl" | "ppl"];
      }>,
    ) {
      state.duplicate.item = {
        ...action.payload.item,
        id: 0,
      };
    },
    cleanDuplication(state) {
      state.duplicate.item = null;
    },
  },
});

export const {
  getStart,
  getSuccess,
  getFailure,
  addCreatedItem,
  cleanData,
  setSuccess,
  setActiveEntity,
  hideWarning,
  cleanActiveEntity,
  hydrateContacts,
  setContactsLoading,
  upsertContact,
  removeContact,
  setContactsError,
  invalidateContactItems,
  setDuplicateServiceItem,
  cleanDuplication,
} = settingsSlice.actions;

export const { reducer } = settingsSlice;
export default reducer;

export const fetchSettingsList =
  <K extends SettingsEntityKey>(
    key: K,
    params?: Record<string, any>,
  ): AppThunk =>
  async (dispatch) => {
    try {
      const config = getEntityConfig(key) as any;
      dispatch(getStart(key));
      const data = await fetchList<SettingsEntityRowMap[K]>(config, params);
      dispatch(getSuccess({ key, items: data.items, total: data.total }));
    } catch (err) {
      const error = getErrorMessageFromResponse(err);
      dispatch(getFailure({ key, error }));
    }
  };
export const duplicateServiceItem =
  (key: "apl" | "ppl", id: number): AppThunk =>
  async (dispatch) => {
    try {
      const config = getEntityConfig(key) as any;
      const data = await fetchItem(config, id);
      dispatch(setDuplicateServiceItem({ key, item: data as any }));
    } catch (err) {
      const error = getErrorMessageFromResponse(err);
      dispatch(getFailure({ key, error }));
    }
  };
export const getEntityById =
  <K extends SettingsEntityKey>(key: K, id: number): AppThunk =>
  async (dispatch) => {
    try {
      const config = getEntityConfig(key) as any;
      const data = await fetchItem<SettingsEntityRowMap[K]>(config, id);
      dispatch(setActiveEntity({ key, item: data }));
    } catch (err) {
      const error = getErrorMessageFromResponse(err);
      dispatch(getFailure({ key, error }));
    }
  };

export const createSettingsItem =
  <K extends SettingsEntityKey>(
    key: K,
    payload: Record<string, any>,
    navigate: any,
  ): AppThunk =>
  async (dispatch) => {
    try {
      const config = getEntityConfig(key) as any;
      const data = await createItem<SettingsEntityRowMap[K]>(config, payload);
      dispatch(setActiveEntity({ key, item: data }));
      navigate(`/settings/${config.path}/${data.id}/view`);
      setTimeout(() => {
        dispatch(setSuccess({ key, success: "Item created successfully." }));
      }, 1000);
    } catch (err) {
      const error = getErrorMessageFromResponse(err);
      dispatch(getFailure({ key, error }));
    }
  };

export const updateSettingsItem =
  <K extends SettingsEntityKey>(
    key: K,
    id: number,
    payload: Record<string, any>,
    navigate: any,
  ): AppThunk =>
  async (dispatch) => {
    try {
      const config = SETTINGS_ENTITIES[key] as any;
      dispatch(getStart(key));
      const data = await updateItem<SettingsEntityRowMap[K]>(
        config,
        id,
        payload as Partial<SettingsEntityRowMap[K]>,
      );
      dispatch(setActiveEntity({ key, item: data }));
      const contactTypesToInvalidate =
        SETTINGS_ENTITY_TO_INVALIDATE_CONTACTS[key];
      if (contactTypesToInvalidate?.length) {
        contactTypesToInvalidate.forEach((type) =>
          dispatch(invalidateContactItems(type)),
        );
      }
      navigate(`/settings/${config.path}/${id}/view`);
      setTimeout(() => {
        dispatch(setSuccess({ key, success: "Item updated successfully." }));
      }, 1000);
    } catch (err) {
      const error = getErrorMessageFromResponse(err);
      dispatch(getFailure({ key, error }));
    }
  };

export const deleteSettingsItem =
  (
    key: SettingsEntityKey,
    id: number,
    navigate: any,
    params?: Record<string, any>,
  ): AppThunk =>
  async (dispatch) => {
    try {
      const config = SETTINGS_ENTITIES[key] as any;
      await deleteItem(config, id);
      const contactTypesToInvalidate =
        SETTINGS_ENTITY_TO_INVALIDATE_CONTACTS[key];
      if (contactTypesToInvalidate?.length) {
        contactTypesToInvalidate.forEach((type) =>
          dispatch(invalidateContactItems(type)),
        );
      }
      if (navigate) {
        navigate(`/settings/${config.path}`);
      } else {
        dispatch(fetchSettingsList(key, params));
      }
      setTimeout(() => {
        dispatch(setSuccess({ key, success: "Item removed successfully." }));
      }, 1000);
    } catch (err) {
      const error = getErrorMessageFromResponse(err);
      dispatch(getFailure({ key, error }));
    }
  };

export const restoreSettingsItem =
  (
    key: SettingsEntityKey,
    id: number,
    params?: Record<string, any>,
    navigate?: any,
  ): AppThunk =>
  async (dispatch) => {
    try {
      const config = SETTINGS_ENTITIES[key] as any;
      await restoreItem(config, id);
      const contactTypesToInvalidate =
        SETTINGS_ENTITY_TO_INVALIDATE_CONTACTS[key];
      if (contactTypesToInvalidate?.length) {
        contactTypesToInvalidate.forEach((type) =>
          dispatch(invalidateContactItems(type)),
        );
      }
      if (navigate) {
        navigate(`/settings/${config.path}`);
      } else {
        dispatch(fetchSettingsList(key, params));
      }
      setTimeout(() => {
        dispatch(setSuccess({ key, success: "Item restored successfully." }));
      }, 1000);
    } catch (err) {
      const error = getErrorMessageFromResponse(err);
      dispatch(getFailure({ key, error }));
    }
  };

const handleContactsError = (err: any, dispatch: (action: any) => void) => {
  const error = getErrorMessageFromResponse(err);
  dispatch(setContactsError(error));
  return error;
};

export const fetchContact =
  (key: ContactEntityKey, parentId: number): AppThunk =>
  async (dispatch, getState) => {
    const state = getState().settings.contactItems;

    if (state.loaded[key][parentId]) {
      return state.items[key][parentId] || [];
    }

    try {
      dispatch(setContactsLoading({ type: key, parentId, loading: true }));
      const res = await fetchContactItemsApi(key, parentId);
      dispatch(hydrateContacts({ type: key, parentId, items: res.items }));
      return res.items;
    } catch (err) {
      throw new Error(handleContactsError(err, dispatch));
    } finally {
      dispatch(setContactsLoading({ type: key, parentId, loading: false }));
    }
  };

export const createContact =
  (key: ContactEntityKey, parentId: number, payload: any): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(setContactsLoading({ type: key, parentId, loading: true }));
      const item = await createContactItemApi(key, payload);
      dispatch(upsertContact({ type: key, parentId, item }));
    } catch (err) {
      throw new Error(handleContactsError(err, dispatch));
    } finally {
      dispatch(setContactsLoading({ type: key, parentId, loading: false }));
    }
  };

export const updateContact =
  (
    key: ContactEntityKey,
    parentId: number,
    itemId: number,
    payload: any,
  ): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(setContactsLoading({ type: key, parentId, loading: true }));
      const item = await updateContactItemApi(key, itemId, payload);
      dispatch(upsertContact({ type: key, parentId, item }));
    } catch (err) {
      throw new Error(handleContactsError(err, dispatch));
    } finally {
      dispatch(setContactsLoading({ type: key, parentId, loading: false }));
    }
  };

export const deleteContact =
  (key: ContactEntityKey, parentId: number, itemId: number): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(setContactsLoading({ type: key, parentId, loading: true }));
      await deleteContactItemApi(key, itemId);
      dispatch(removeContact({ type: key, parentId, itemId }));
    } catch (err) {
      throw new Error(handleContactsError(err, dispatch));
    } finally {
      dispatch(setContactsLoading({ type: key, parentId, loading: false }));
    }
  };

export const selectContactItems = (
  state: RootState,
  key: ContactEntityKey,
  parentId: number,
) => state.settings.contactItems.items[key][parentId] || [];

export const selectContactLoading = (
  state: RootState,
  key: ContactEntityKey,
  parentId: number,
) => state.settings.contactItems.loading[key][parentId] || false;

export const getDuplicateServiceItem = (state: RootState) =>
  state.settings.duplicate.item;
